import React, { useEffect, useState } from 'react';
import {
	Alert,
	Button,
	CircularProgress,
	IconButton,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { DataGrid, esES } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { deleteProvider, getProviders } from '../../actions/provider';
import { SearcherProvider } from '../searcher/SearcherProvider';
import { formatDateTimeMX } from '../../helpers/hours';
import { handleConfirmModal, handleError, rowStyle } from '../../helpers/utils';
import { columnStyleCell } from '../orderTracking/BiasTable';

const defaultEndpoint =
	'/api/client/provider/?ordering=-created_at&page_size=5';
const searchIconStyle = { color: '#143f6c', cursor: 'pointer' };

// Providers table
export const ProvidersTable = ({ data = [] }) => {
	// Hook for navigation
	const navigate = useNavigate();

	// Dispatch
	const dispatch = useDispatch();

	const { user_type } = useSelector((state) => state.user);

	// Show searcher or not
	const [showSearcher, setShowSearcher] = useState(false);

	// Loading state
	const [isLoading, setLoading] = useState(false);

	// Rows
	const [rowsState, setRowsState] = useState({
		isLoading: false,
		data: [],
		total: 0,
		page: 1,
		lastPage: 1,
		pageSize: 5,
		next: defaultEndpoint,
		previous: null,
	});

	const generateRows = async (newData = false) => {
		let newProviders = [];
		let total = 0;
		let data = null;

		const moveForward = rowsState.page >= rowsState.lastPage;

		setLoading(true);

		if (newData) {
			newProviders = newData.results;
			total = newData.count;
		} else {
			data = (
				await getProviders(
					newData === null
						? defaultEndpoint
						: moveForward
						? rowsState.next
						: rowsState.previous
				)
			).data;
			newProviders = data.results;
			total = data.count;
		}

		if (newProviders?.length === 0) {
			setRowsState((old) => ({
				...old,
				isLoading: false,
				data: [],
				total: 0,
				next: data?.next,
				previous: data?.previous,
			}));
		} else {
			const res =
				newProviders?.map((item) => ({
					...item,
					company_name: item.name.toUpperCase(),
					created_at: formatDateTimeMX(item.created_at),
				})) || [];

			setRowsState((old) => ({
				...old,
				isLoading: false,
				data: res,
				total: total,
				pageSize: newData === null ? 5 : old.pageSize,
				next: data?.next,
				previous: data?.previous,
			}));
		}

		setLoading(false);
	};

	// Handle delete user with modal confirmation
	const handleDeleteUser = (userId) => {
		handleConfirmModal(
			'¡Precaución!',
			'¿Estás seguro de eliminar permanentemente al proveedor del sistema?',
			'warning',
			'Sí, eliminar'
		)
			.then(({ isConfirmed }) => {
				if (isConfirmed) {
					dispatch(deleteProvider(userId));
				}
			})
			.catch((error) => handleError(error));
	};

	useEffect(() => {
		generateRows();
	}, [rowsState.page, rowsState.pageSize]);

	// Define columns
	const columns = [
		{
			field: 'col8',
			headerName: 'Acciones',
			width: 150,
			...columnStyleCell,
			// Add actions to each case
			renderCell: ({ row }) => (
				<>
					<Tooltip title='Ver perfil de proveedor'>
						<IconButton
							style={{ color: '#143f6c' }}
							aria-label='profile'
							color='primary'
							onClick={() => {
								navigate('/dashboard/detalle-proveedor', {
									state: { providerData: row },
								});
							}}
						>
							<PersonIcon />
						</IconButton>
					</Tooltip>
					{[1, 2, 5].includes(user_type.id) && (
						<Tooltip title='Editar proveedor'>
							<IconButton
								style={{ color: '#143f6c' }}
								aria-label='profile'
								color='primary'
								onClick={() => {
									navigate('/dashboard/editar-proveedor', {
										state: { providerData: row },
									});
								}}
							>
								<EditIcon />
							</IconButton>
						</Tooltip>
					)}
					{user_type.id === 2 && (
						<Tooltip title='Eliminar proveedor'>
							<IconButton
								style={{ color: '#143f6c' }}
								aria-label='delete'
								color='primary'
								onClick={() => handleDeleteUser(row.id)}
							>
								<DeleteIcon />
							</IconButton>
						</Tooltip>
					)}
				</>
			),
		},
		{
			field: 'id',
			headerName: 'ID',
			width: 70,
			...columnStyleCell,
			renderCell: ({ row }) => (
				<Tooltip title={row.id}>
					<span className='table-cell-trucate'>{row.id}</span>
				</Tooltip>
			),
		},
		{
			field: 'name',
			headerName: 'Proveedor',
			width: 250,
			...columnStyleCell,
			renderCell: ({ row }) => (
				<Tooltip title={row.name}>
					<span className='table-cell-trucate'>{row.name}</span>
				</Tooltip>
			),
		},
		{
			field: 'phone_number',
			headerName: 'Número telefónico',
			width: 150,
			...columnStyleCell,
			renderCell: ({ row }) => (
				<Tooltip title={row.phone_number}>
					<span className='table-cell-trucate'>
						{row.phone_number}
					</span>
				</Tooltip>
			),
		},
		{
			field: 'email',
			headerName: 'Correo electrónico',
			width: 200,
			...columnStyleCell,
			renderCell: ({ row }) => (
				<Tooltip title={row.email}>
					<span className='table-cell-trucate'>{row.email}</span>
				</Tooltip>
			),
		},
		{
			field: 'created_at',
			headerName: 'Creado',
			width: 200,
			...columnStyleCell,
			renderCell: ({ row }) => (
				<Tooltip title={row.created_at}>
					<span className='table-cell-trucate'>{row.created_at}</span>
				</Tooltip>
			),
		},
	];

	return (
		<div
			style={{
				padding: '20px',
			}}
		>
			<Stack
				direction='row'
				alignItems='flex-end'
				justifyContent='space-between'
				style={{
					border: '1px solid white',
					padding: '20px 20px 0 0',
					margin: '0 0 40px 0',
					width: '100%',
					...columnStyleCell,
				}}
			>
				<Typography variant='h4' gutterBottom>
					Proveedores
				</Typography>
				<Button
					variant='contained'
					style={{
						backgroundColor: '#143f6c',
					}}
					onClick={() => {
						navigate('/dashboard/nuevo-proveedor');
					}}
				>
					Nuevo proveedor
				</Button>
			</Stack>

			<hr />

			<Typography
				variant='h6'
				gutterBottom
				style={{ ...rowStyle, margin: '20px 0 10px 0' }}
			>
				Búsqueda global{' '}
				<div
					onClick={() => setShowSearcher(!showSearcher)}
					style={{ margin: '0 10px' }}
				>
					{showSearcher ? (
						<SearchOffIcon style={searchIconStyle} />
					) : (
						<SearchIcon style={searchIconStyle} />
					)}
				</div>
			</Typography>

			{/* Order searcher */}
			{showSearcher && (
				<SearcherProvider
					generateRows={generateRows}
					handleClose={() => setShowSearcher(false)}
					rowsState={rowsState}
					setRowsState={setRowsState}
				/>
			)}

			{isLoading ? (
				<center style={{ padding: '20%' }}>
					<CircularProgress />
				</center>
			) : (
				<>
					{/* Datagrid table */}
					{rowsState.data.length > 0 ? (
						<DataGrid
							rows={rowsState.data}
							rowCount={rowsState.total}
							loading={rowsState.isLoading}
							rowsPerPageOptions={[5, 10, 20]}
							pagination
							autoHeight
							page={rowsState.page - 1}
							pageSize={rowsState.pageSize}
							paginationMode='server'
							onPageChange={(newPage) => {
								setRowsState((old) => ({
									...old,
									page: newPage + 1,
									lastPage: old.page,
								}));
							}}
							onPageSizeChange={(newPage) => {
								if (showSearcher) {
									setRowsState((old) => ({
										...old,
										pageSize: newPage,
										next: `${old.current}&page_size=${newPage}`,
										page: 1,
										lastPage: 1,
									}));
								} else {
									setRowsState((old) => ({
										...old,
										pageSize: newPage,
										next: `/api/client/provider/?ordering=-created_at&page_size=${newPage}`,
										page: 1,
										lastPage: 1,
									}));
								}
							}}
							columns={columns}
							localeText={
								esES.components.MuiDataGrid.defaultProps
									.localeText
							}
						/>
					) : (
						<Alert severity='info'>No hay ningún proveedor</Alert>
					)}
				</>
			)}
		</div>
	);
};
